/*
* === AboutUsPage.js (Updated - Funder Acquisition Focus) ===
*/
import React from 'react';
import { 
    Container, Typography, Box, Grid, Button, Paper, Avatar, Divider, Stack 
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import SchoolIcon from '@mui/icons-material/School';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import { Link } from 'react-router-dom';

const AboutUsPage = () => {
    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pb: 8 }}>
            
            {/* 1. Hero Section */}
            <Box sx={{ 
                bgcolor: 'primary.main', 
                color: 'white', 
                py: { xs: 8, md: 12 }, 
                textAlign: 'center',
                mb: 6
            }}>
                <Container maxWidth="md">
                    <Typography variant="h2" fontWeight="800" gutterBottom>
                        Bridging the Gap Between <br/> Ambition and Opportunity
                    </Typography>
                    <Typography variant="h5" sx={{ opacity: 0.9, mb: 4, fontWeight: 300 }}>
                        BrightSparc is South Africa's premier bursary management platform, connecting forward-thinking funders with the country's brightest young minds.
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Button 
                            variant="contained" 
                            color="secondary" 
                            size="large" 
                            component={Link} 
                            to="/register?tier=GROWTH" // Link to Funder Registration
                            sx={{ fontWeight: 'bold', px: 4 }}
                        >
                            Partner With Us (Funders)
                        </Button>
                        <Button 
                            variant="outlined" 
                            color="inherit" 
                            size="large" 
                            component={Link} 
                            to="/register"
                        >
                            Apply for Funding
                        </Button>
                    </Stack>
                </Container>
            </Box>

            <Container maxWidth="lg">
                
                {/* 2. Value Proposition Grid */}
                <Grid container spacing={4} sx={{ mb: 8 }}>
                    <Grid item xs={12} md={6}>
                        <Paper elevation={3} sx={{ p: 4, height: '100%', borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main', mr: 2 }}>
                                    <BusinessIcon />
                                </Avatar>
                                <Typography variant="h5" fontWeight="bold">For Funders & CSI Teams</Typography>
                            </Box>
                            <Typography variant="body1" color="text.secondary" paragraph>
                                Managing a bursary fund shouldn't be an administrative nightmare. BrightSparc provides a secure, compliant, and efficient command center for your CSI initiatives.
                            </Typography>
                            <Divider sx={{ my: 2 }} />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <VerifiedUserIcon color="success" sx={{ mr: 1 }} />
                                    <Typography variant="body2"><strong>POPIA Compliant:</strong> Secure student data handling with audit trails.</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <TrendingUpIcon color="secondary" sx={{ mr: 1 }} />
                                    <Typography variant="body2"><strong>B-BBEE Reporting:</strong> Instant demographic and financial reports.</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <PeopleIcon color="primary" sx={{ mr: 1 }} />
                                    <Typography variant="body2"><strong>Streamlined Review:</strong> Review 100s of applications in minutes with automated scoring.</Typography>
                                </Box>
                            </Box>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Paper elevation={3} sx={{ p: 4, height: '100%', borderRadius: 2, bgcolor: 'grey.50' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Avatar sx={{ bgcolor: 'secondary.light', color: 'secondary.main', mr: 2 }}>
                                    <SchoolIcon />
                                </Avatar>
                                <Typography variant="h5" fontWeight="bold">For Students</Typography>
                            </Box>
                            <Typography variant="body1" color="text.secondary" paragraph>
                                We believe talent is distributed equally, but opportunity is not. BrightSparc removes the barriers to entry for funding.
                            </Typography>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="body2" paragraph>
                                Stop filling out the same form 50 times. Create your profile once, verify your documents in our Digital Vault, and apply to multiple verified funders with a single click.
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" color="primary">
                                100% Free for Applicants. Always.
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>

                {/* 3. Mission Statement */}
                <Box sx={{ textAlign: 'center', mb: 8, maxWidth: 800, mx: 'auto' }}>
                    <Typography variant="h4" fontWeight="bold" gutterBottom>Our Mission</Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        "To democratize access to education funding in Africa by building the most trusted, transparent, and efficient connection between capital and talent."
                    </Typography>
                </Box>

                {/* 4. Call to Action Footer */}
                <Paper 
                    elevation={0} 
                    sx={{ 
                        p: 6, 
                        textAlign: 'center', 
                        borderRadius: 4, 
                        background: 'linear-gradient(45deg, #1e1e1e 30%, #424242 90%)',
                        color: 'white'
                    }}
                >
                    <Typography variant="h4" fontWeight="bold" gutterBottom>
                        Ready to make an impact?
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 4, opacity: 0.8 }}>
                        Join the growing network of companies using BrightSparc to manage their education funds.
                    </Typography>
                    <Button 
                        variant="contained" 
                        color="secondary" 
                        size="large" 
                        component={Link}
                        to="/register?tier=GROWTH"
                        sx={{ px: 6, py: 1.5, fontSize: '1.1rem', fontWeight: 'bold' }}
                    >
                        Register Your Organization
                    </Button>
                </Paper>

            </Container>
        </Box>
    );
};

export default AboutUsPage;